import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { candidatesApi, formatApiError } from '../../lib/api';
import { hasScore } from '../../lib/score';
import { Button } from '../../components/ui/button';
import { 
    ArrowLeft, Eye, Brain, Loader2, CheckCircle, Users, ChevronDown
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function RecentCandidatesPage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const candidateIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        if (candidateIds.length === 0) {
            navigate('/dashboard/candidates');
            return;
        }
        loadCandidates();
    }, []);

    const loadCandidates = async () => {
        try {
            const results = await Promise.all(
                candidateIds.map(id => candidatesApi.getById(id).catch(() => null))
            );
            setCandidates(results.filter(r => r !== null).map(r => r.data));
        } catch (error) {
            toast.error(t('errors.generic_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async (candidateId) => {
        setAnalyzing(prev => ({ ...prev, [candidateId]: true }));
        try {
            await candidatesApi.analyze(candidateId);
            toast.success(t('candidates.analyze_success'));
            loadCandidates();
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setAnalyzing(prev => ({ ...prev, [candidateId]: false }));
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return { bg: 'bg-[#D1FAE5]', text: 'text-[#059669]' };
        if (score >= 60) return { bg: 'bg-[#DBEAFE]', text: 'text-[#2563EB]' };
        if (score >= 40) return { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]' };
        return { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]' };
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'analyzed':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">{t('candidates.badge_analyzed')}</span>;
            case 'pending':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#D97706]">{t('candidates.badge_pending')}</span>;
            case 'analysis_failed':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEE2E2] text-[#DC2626]">{t('candidates.badge_failed')}</span>;
            default:
                return null;
        }
    };

    const subtitleKey = candidates.length <= 1 ? 'candidates.recent_subtitle_one' : 'candidates.recent_subtitle_other';

    return (
        <div className="space-y-6" data-testid="recent-candidates-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/dashboard/candidates')}
                        className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                        data-testid="back-to-candidates-btn"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#4B5563]" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] flex items-center gap-2">
                            <CheckCircle className="w-6 h-6 text-[#10B981]" />
                            {t('candidates.recent_title')}
                        </h1>
                        <p className="text-[#4B5563]">
                            {t(subtitleKey, { count: candidates.length })}
                        </p>
                    </div>
                </div>
                <Link to="/dashboard/candidates">
                    <Button variant="outline" data-testid="view-all-candidates-btn">
                        <Users className="w-4 h-4 mr-2" />
                        {t('candidates.recent_view_all')}
                    </Button>
                </Link>
            </div>

            {/* Success banner */}
            <div className="bg-[#D1FAE5] border border-[#10B981]/20 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#059669] flex-shrink-0" />
                <p className="text-[#065F46] text-sm">
                    {t('candidates.recent_banner')}
                </p>
            </div>

            {/* Candidates list */}
            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
                </div>
            ) : candidates.length > 0 ? (
                <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider">
                                    {t('candidates.col_candidate')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden md:table-cell">
                                    {t('candidates.col_skills')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden lg:table-cell">
                                    {t('candidates.col_job')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider">
                                    {t('candidates.col_score')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-[#4B5563] uppercase tracking-wider hidden sm:table-cell">
                                    {t('candidates.col_status')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-[#4B5563] uppercase tracking-wider">
                                    {t('common.actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E5E7EB]">
                            {candidates.map((candidate) => {
                                const scoreColors = hasScore(candidate.score?.overall) ? getScoreColor(candidate.score.overall) : null;
                                return (
                                    <tr 
                                        key={candidate.id} 
                                        className="hover:bg-[#F9FAFB] transition-colors"
                                        data-testid={`recent-candidate-row-${candidate.id}`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-[#002FA7] flex items-center justify-center text-white font-medium">
                                                    {candidate.name?.charAt(0)?.toUpperCase() || '?'}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-[#0A0A0A]">{candidate.name}</p>
                                                    <p className="text-sm text-[#9CA3AF]">{candidate.email || candidate.cv_filename}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell">
                                            <div className="flex flex-wrap gap-1">
                                                {candidate.skills?.slice(0, 3).map((skill, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-[#F3F4F6] text-[#4B5563] text-xs rounded">
                                                        {skill}
                                                    </span>
                                                ))}
                                                {candidate.skills?.length > 3 && (
                                                    <span className="text-xs text-[#9CA3AF]">+{candidate.skills.length - 3}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 hidden lg:table-cell">
                                            {candidate.matched_job_title ? (
                                                <span className="px-2 py-1 bg-[#DBEAFE] text-[#002FA7] text-xs rounded-full">
                                                    {candidate.matched_job_title}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 bg-[#F3F4F6] text-[#9CA3AF] text-xs rounded-full italic">
                                                    {t('candidates.unassigned')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {hasScore(candidate.score?.overall) ? (
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${scoreColors.bg} ${scoreColors.text}`} data-testid={`recent-candidate-score-${candidate.id}`}>
                                                    {candidate.score.overall}
                                                </span>
                                            ) : (
                                                <span className="text-[#9CA3AF]">-</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 hidden sm:table-cell">
                                            {getStatusBadge(candidate.status)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {candidate.status === 'pending' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleAnalyze(candidate.id)}
                                                        disabled={analyzing[candidate.id]}
                                                        data-testid={`analyze-recent-btn-${candidate.id}`}
                                                    >
                                                        {analyzing[candidate.id] ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Brain className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                )}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button size="sm" variant="ghost">
                                                            <ChevronDown className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => navigate(`/dashboard/candidates/${candidate.id}`)}>
                                                            <Eye className="w-4 h-4 mr-2" />
                                                            {t('jobs.view_details')}
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
                    <Users className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" />
                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('candidates.recent_empty_title')}
                    </h3>
                    <p className="text-[#4B5563] mt-2">
                        {t('candidates.recent_empty_message')}
                    </p>
                    <Link to="/dashboard/candidates">
                        <Button className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white">
                            {t('candidates.recent_back')}
                        </Button>
                    </Link>
                </div>
            )}
        </div>
    );
}
